import React from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { getDisplayName } from 'lane-shared/helpers';
import { imageUrl } from 'lane-shared/helpers/formatters';
import { GuestInviteGuestType } from 'lane-shared/renderers/v5/features/types/GuestInviteGuestType';
import { PublicProfileType, PublicUserType } from 'lane-shared/types/User';

import Input from 'components/form/Input';
import CircleListView from 'components/lane/CircleListView';
import { H5 } from 'components/typography';

import styles from './GuestInviteMemberSearch.scss';

type User = Pick<PublicUserType, '_id' | 'name'> & {
  profile: Pick<PublicProfileType, 'name' | 'image' | 'logo'>;
};

type GuestInviteMemberSearchProps = {
  onUserSelected: (user: User) => void;
  selectedGuests: GuestInviteGuestType[];
  users: User[];
  search: string;
  setSearch: (value: string) => void;
  disabled?: boolean;
};

export default function GuestInviteMemberSearch({
  onUserSelected,
  selectedGuests,
  users,
  search,
  setSearch,
  disabled = false,
}: GuestInviteMemberSearchProps) {
  const { t } = useTranslation();
  const selectedIds = selectedGuests?.map(i => i?.user?._id);

  return (
    <div className={styles.GuestInviteMemberSearch}>
      <div className={styles.searchBar}>
        <Input
          showClear
          icon="search"
          iconSet="FontAwesome"
          iconType="fal"
          value={search}
          onChange={value => setSearch(value)}
          placeholder={t('Search…')}
        />
      </div>
      {users.map(user => {
        const isSelected = selectedIds?.some(id => id === user?._id);
        const isDisabled = disabled && !isSelected;

        return (
          <CircleListView
            key={user._id}
            className={cx(styles.listView, isDisabled && styles.listViewDisabled)}
            image={imageUrl(user.profile?.image)}
            logo={imageUrl(user.profile?.logo)}
            name={getDisplayName(user)}
            onClick={isDisabled ? () => {} : () => onUserSelected(user)}
            selected={isSelected}
            isSelectable
            aria-disabled={isDisabled}
          >
            <H5 bold>{getDisplayName(user)}</H5>
          </CircleListView>
        );
      })}
    </div>
  );
}
